// pkg195 — payment-import shapes match the merged backend.
// POST /api/admin/payment-import/preview  { csv, mappings?, agreement_dates? }
//   mappings is { [csv client name]: client_id }
//   agreement_dates is { [client_id]: 'YYYY-MM-DD' }
//   response rows[].bucket is will_import | duplicate | before_sign_on |
//   no_client_match | no_agreement_date | skipped
// POST /api/admin/payment-import/commit { preview_token, include, mappings?, agreement_dates? }
//   include is external_ref values. Only rows that classify as will_import are inserted.
//   response is { inserted, payment_ids, already_on_file, not_imported, method, method_label,
//   deposit_account, applied_to_invoices } — delivered 2026-10-31

export const IMPORT_BUCKETS = [
  {
    key: 'will_import',
    label: 'Will import',
    hint: 'These are ready to save. Uncheck any row you want to leave out. Each one is recorded as Unknown, in the operating account, and is not applied to an invoice.',
    selectable: true,
  },
  {
    key: 'duplicate',
    label: 'Possible duplicates',
    hint: 'These match a payment already on file, or the same email appears twice in this file. They will not be saved again.',
    selectable: false,
  },
  {
    key: 'before_sign_on',
    label: 'Before agreement date',
    hint: 'The payment date is earlier than the date the client signed the agreement. These will not be saved.',
    selectable: false,
  },
  {
    key: 'no_agreement_date',
    label: 'No agreement date',
    hint: 'This client has no signed agreement on file. Enter the date you and the client agreed to work together. That date is used for this import only. It is not saved as a signature.',
    selectable: false,
  },
  {
    key: 'no_client_match',
    label: 'No client match',
    hint: 'The name in the file does not match one client. Choose the client. If several people share that name, the list shows them.',
    selectable: false,
  },
  {
    key: 'skipped',
    label: 'Skipped',
    hint: 'These will not be saved. The note says why.',
    selectable: false,
  },
];

const BUCKET_KEYS = new Set(IMPORT_BUCKETS.map((b) => b.key));

export const REASON_TEXT = {
  not_counted: 'This row was marked as not counted.',
  missing_client: 'The client name is blank.',
  invalid_date: 'The date is not a real calendar date.',
  invalid_amount: 'The amount could not be read.',
  zero_amount: 'The amount is zero.',
  negative_refund_unsupported: 'This is a refund. Refunds in this file are not imported.',
  missing_thread_id: 'This row has no email thread id, so it cannot be saved.',
  mapped_client_not_found: 'The client you chose is not in the firm.',
  ambiguous_name: 'More than one client has this name.',
  no_exact_name: 'No client has this exact name.',
  external_ref: 'This email was already imported.',
  in_file: 'This email appears more than once in the file.',
  amount_within_3_days: 'A payment for this client and amount, within 3 days of this date, is already on file.',
  no_agreement_date: 'This client has no signed agreement date.',
  before_sign_on: 'This payment is dated before the client signed the agreement.',
  not_in_preview: 'This row was not in the preview.',
};

export function reasonText(code) {
  if (!code) return '';
  return REASON_TEXT[code] || String(code);
}

export function buildPreviewBody({ csv, mappings, agreement_dates }) {
  const body = { csv };
  if (mappings && Object.keys(mappings).length) body.mappings = mappings;
  if (agreement_dates && Object.keys(agreement_dates).length) body.agreement_dates = agreement_dates;
  return body;
}

export function buildCommitBody({ preview_token, mappings, agreement_dates, include }) {
  const body = { preview_token, include };
  if (mappings && Object.keys(mappings).length) body.mappings = mappings;
  if (agreement_dates && Object.keys(agreement_dates).length) body.agreement_dates = agreement_dates;
  return body;
}

export function groupPreview(data) {
  const payload = data || {};
  const buckets = {};
  IMPORT_BUCKETS.forEach((bucket) => { buckets[bucket.key] = []; });
  const rows = Array.isArray(payload.rows) ? payload.rows : [];
  rows.forEach((row) => {
    const key = BUCKET_KEYS.has(row.bucket) ? row.bucket : 'skipped';
    buckets[key].push(row);
  });
  const counts = payload.counts || {};
  const totals = {};
  IMPORT_BUCKETS.forEach((bucket) => {
    totals[bucket.key] = buckets[bucket.key].reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
  });
  return {
    preview_token: payload.preview_token,
    expires_at: payload.expires_at,
    method: payload.method,
    method_label: payload.method_label,
    deposit_account: payload.deposit_account,
    applied_to_invoices: payload.applied_to_invoices,
    refunds: payload.refunds || null,
    ignored_agreement_dates: Array.isArray(payload.ignored_agreement_dates) ? payload.ignored_agreement_dates : [],
    counts,
    buckets,
    totals,
  };
}

export function selectedRefs(selected) {
  return Object.keys(selected).filter((ref) => selected[ref]);
}

// Keep the owner's checks. A will_import row that was not in the previous
// selection (a name or date they just fixed) starts checked.
export function nextSelection(previous, willImportRows) {
  const next = {};
  (willImportRows || []).forEach((row) => {
    if (!row.external_ref) return;
    const ref = String(row.external_ref);
    if (previous && Object.prototype.hasOwnProperty.call(previous, ref)) next[ref] = !!previous[ref];
    else next[ref] = true;
  });
  return next;
}

export function findRow(preview, externalRef) {
  if (!preview || externalRef == null) return null;
  const ref = String(externalRef);
  const keys = Object.keys(preview.buckets);
  for (let i = 0; i < keys.length; i += 1) {
    const hit = preview.buckets[keys[i]].find((row) => String(row.external_ref) === ref);
    if (hit) return hit;
  }
  return null;
}
